import constants from '@/shared/constants/constants';
import { getFileNameGenerate } from '@/shared/functions';
import {
  getAutoGenerate30StatusApi,
  extractProductInformationDocumentApi,
  createProjectAutoGenerate30Api,
  generateImageGenerate30TestApi,
  generateImageGenerate30RemainingApi,
  getAutoGenerate30ResultsApi,
  downloadZipProjectGenerate,
  downloadFileProjectGenerate,
  progressGenerate,
  deletingBackgroundApi,
} from '@/services/project.service';

const REFLECTION_LEVEL_DEFAULT = 3;
const PROGRESS_POLL_INTERVAL_MS = 5000;
const PROGRESS_POLL_TIMEOUT_MS = 40 * 60 * 1000;
const REMAINING_RESULTS_POLL_TIMEOUT_MS = 24 * 60 * 60 * 1000;
const OUTPUT_IMAGE_COUNT = constants.AUTO_GENERATE_30.TEST_IMAGE_COUNT;
const TOTAL_OUTPUT_IMAGE_COUNT = constants.AUTO_GENERATE_30.TEST_IMAGE_COUNT + constants.AUTO_GENERATE_30.REMAINING_IMAGE_COUNT;

const emptyForm = () => ({
  category_shape: '',
  concept: '',
  brand: '',
  naming: '',
  catch_copy: '',
  additional_instructions: '',
  reflection_level: REFLECTION_LEVEL_DEFAULT,
  reference_image: null,
  reference_image_preview: null,
});

let autoGenerate30ProgressPollToken = null;
let autoGenerate30RemainingResultsPollToken = null;
let autoGenerate30SessionEpoch = 0;
let autoGenerate30StatusRequestId = 0;
let ag30RemainingPollInterval = null;
let ag30RemainingPollTimeout = null;

const emptyAutoGenerate30Results = () => ({
  status: null,
  progress: 0,
  processed_images: 0,
  projectId: null,
  projectName: null,
  projectGenerateImageId: null,
  images: [],
  errorMessage: null,
  forbidden: false,
  estimatedMinutes: null,
});

const createInitialState = () => ({
  projectTypeCreated: [],
  typeExportPdf: '',
  autoGenerate30Status: null,
  autoGenerate30Form: emptyForm(),
  autoGenerate30ExtractLoading: false,
  autoGenerate30TestGenerateLoading: false,
  autoGenerate30TestPollActive: false,
  autoGenerate30TestRunLocked: false,
  autoGenerate30Progress: 0,
  autoGenerate30OutputImages: [],
  autoGenerate30InputSnapshot: null,
  autoGenerate30InputSnapshotAtClick: null,
  autoGenerate30TestErrorMessage: null,
  autoGenerate30SessionProjectId: null,
  autoGenerate30ProjectGenerateImageId: null,
  autoGenerate30Results: emptyAutoGenerate30Results(),
  autoGenerate30RemainingPollActive: false,
  autoGenerate30RemainingSubmitLocked: false,
});

function normalizeBackendMessage(data) {
  if (!data || data.message == null) {
    return null;
  }
  return Array.isArray(data.message) ? data.message[0] : data.message;
}

function buildAutoGenerate30GenerateFields(form) {
  return {
    category_shape: form.category_shape || '',
    concept: form.concept || '',
    brand: form.brand || '',
    naming: form.naming || '',
    catch_copy: form.catch_copy || '',
    additional_instructions: form.additional_instructions || '',
    reflection_level: String(form.reflection_level ?? REFLECTION_LEVEL_DEFAULT),
  };
}

function stopAutoGenerate30IntervalProgress(commit) {
  if (ag30PollInterval) {
    clearInterval(ag30PollInterval);
    ag30PollInterval = null;
  }
  if (ag30PollTimeout) {
    clearTimeout(ag30PollTimeout);
    ag30PollTimeout = null;
  }
  if (commit) {
    commit('SET_AUTO_GENERATE_30_TEST_POLL_ACTIVE', false);
  }
}

let ag30PollInterval = null;
let ag30PollTimeout = null;
let ag30PollOutcomePromise = null;
let ag30PollOutcomeResolver = null;
let ag30PollLastProgress = 0;
let ag30IdLivePreview = 1;

function abortAutoGenerate30ProgressPoll(commit) {
  if (autoGenerate30ProgressPollToken) {
    autoGenerate30ProgressPollToken.aborted = true;
    autoGenerate30ProgressPollToken = null;
  }
  stopAutoGenerate30IntervalProgress(commit);
  if (ag30PollOutcomeResolver) {
    ag30PollOutcomeResolver({ outcome: 'aborted', data: null });
    ag30PollOutcomeResolver = null;
    ag30PollOutcomePromise = null;
  }
}

/**
 * One progress tick — same role as sdMixins.getProgress for auto_generate_30.
 * Returns a terminal result object, or null while still PROGRESS.
 */
async function getAutoGenerate30Progress(projectGenerateImageId, commit, pollToken) {
  if (pollToken.aborted) {
    return { outcome: 'aborted', data: null };
  }

  const formData = new FormData();
  formData.append('generate_image_id', String(projectGenerateImageId));
  formData.append('live_preview', 'true');
  formData.append('id_live_preview', String(ag30IdLivePreview));

  const { status, data } = await progressGenerate(formData);
  if (pollToken.aborted) {
    return { outcome: 'aborted', data: null };
  }

  if (status !== constants.HTTP_OK || !data) {
    return null;
  }

  if (data.id_live_preview && Array.isArray(data.live_preview) && data.live_preview.length > 0) {
    ag30IdLivePreview = data.id_live_preview;
  }

  const progress = typeof data.progress === 'number' ? data.progress : parseFloat(data.progress) || 0;
  if (progress >= ag30PollLastProgress) {
    ag30PollLastProgress = progress;
    commit('SET_AUTO_GENERATE_30_PROGRESS', progress);
  }

  const taskStatus = data.status;
  if (taskStatus === constants.PROJECT_STATUS_CANCEL) {
    return { outcome: 'cancelled', data };
  }
  if (taskStatus === constants.PROJECT_STATUS_FAILURE) {
    return { outcome: 'failure', data };
  }
  if (taskStatus === constants.PROJECT_STATUS_SUCCESS) {
    const images = Array.isArray(data.live_preview) ? data.live_preview.filter(Boolean) : [];
    // Giống getProgress: SUCCESS là trạng thái xong; chờ đủ 3 URL preview (poll lần 1 có thể 500/ preview chưa sẵn).
    if (images.length >= OUTPUT_IMAGE_COUNT) {
      return { outcome: 'success', images, data };
    }
    commit('SET_AUTO_GENERATE_30_PROGRESS', 100);
    return null;
  }
  return null;
}

/** Same role as sdMixins.createIntervalProgress — poll until terminal status. */
function createAutoGenerate30IntervalProgress(projectGenerateImageId, commit) {
  abortAutoGenerate30ProgressPoll(commit);
  ag30PollLastProgress = 0;
  ag30IdLivePreview = 1;
  const pollToken = { aborted: false };
  autoGenerate30ProgressPollToken = pollToken;
  commit('SET_AUTO_GENERATE_30_TEST_POLL_ACTIVE', true);

  ag30PollOutcomePromise = new Promise((resolve) => {
    ag30PollOutcomeResolver = resolve;
  });

  let isNext = true;
  const runTick = async () => {
    if (pollToken.aborted || !isNext) {
      return;
    }
    isNext = false;
    const terminal = await getAutoGenerate30Progress(projectGenerateImageId, commit, pollToken);
    isNext = true;
    if (terminal) {
      stopAutoGenerate30IntervalProgress(commit);
      if (ag30PollOutcomeResolver) {
        ag30PollOutcomeResolver(terminal);
        ag30PollOutcomeResolver = null;
      }
    }
  };

  ag30PollInterval = setInterval(runTick, PROGRESS_POLL_INTERVAL_MS);
  runTick();
  ag30PollTimeout = setTimeout(() => {
    stopAutoGenerate30IntervalProgress(commit);
    if (ag30PollOutcomeResolver) {
      ag30PollOutcomeResolver({ outcome: 'timeout', data: null });
      ag30PollOutcomeResolver = null;
    }
  }, PROGRESS_POLL_TIMEOUT_MS);
}

function stopAutoGenerate30RemainingResultsPoll(commit) {
  if (autoGenerate30RemainingResultsPollToken) {
    autoGenerate30RemainingResultsPollToken.aborted = true;
    autoGenerate30RemainingResultsPollToken = null;
  }
  if (ag30RemainingPollInterval) {
    clearInterval(ag30RemainingPollInterval);
    ag30RemainingPollInterval = null;
  }
  if (ag30RemainingPollTimeout) {
    clearTimeout(ag30RemainingPollTimeout);
    ag30RemainingPollTimeout = null;
  }
  if (commit) {
    commit('SET_AUTO_GENERATE_30_REMAINING_POLL_ACTIVE', false);
  }
}

function applyAutoGenerate30ResultsPayload(commit, projectId, data) {
  const estimatedMinutes = data.estimated_minutes != null
    ? Number(data.estimated_minutes) || null
    : undefined;
  commit('SET_AUTO_GENERATE_30_RESULTS', {
    status: data.status,
    progress: data.progress,
    processed_images: data.processed_images,
    projectId: data.project_id || projectId,
    projectName: data.project_name || null,
    projectGenerateImageId: data.project_generate_image_id,
    images: Array.isArray(data.images) ? data.images : [],
    errorMessage: data.error_message || null,
    forbidden: false,
    ...(estimatedMinutes !== undefined ? { estimatedMinutes } : {}),
  });
}

function markAutoGenerate30ResultsForbidden(commit) {
  stopAutoGenerate30RemainingResultsPoll(commit);
  commit('SET_AUTO_GENERATE_30_RESULTS', {
    ...emptyAutoGenerate30Results(),
    forbidden: true,
  });
  commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', false);
}

function createAutoGenerate30RemainingResultsPoll(projectId, commit) {
  stopAutoGenerate30RemainingResultsPoll(commit);
  const pollToken = { aborted: false };
  autoGenerate30RemainingResultsPollToken = pollToken;
  commit('SET_AUTO_GENERATE_30_REMAINING_POLL_ACTIVE', true);

  const runTick = async () => {
    if (pollToken.aborted) {
      return;
    }
    const { status, data } = await getAutoGenerate30ResultsApi(projectId);
    if (pollToken.aborted) {
      return;
    }
    if (status === constants.HTTP_PERMISSION_DENIED) {
      markAutoGenerate30ResultsForbidden(commit);
      return;
    }
    if (status !== constants.HTTP_OK || !data) {
      return;
    }
    applyAutoGenerate30ResultsPayload(commit, projectId, data);
    const taskStatus = data.status;
    const imageCount = Array.isArray(data.images) ? data.images.length : 0;
    if (taskStatus === constants.PROJECT_STATUS_SUCCESS && imageCount >= TOTAL_OUTPUT_IMAGE_COUNT) {
      stopAutoGenerate30RemainingResultsPoll(commit);
      return;
    }
    if (taskStatus === constants.PROJECT_STATUS_FAILURE || taskStatus === constants.PROJECT_STATUS_CANCEL) {
      stopAutoGenerate30RemainingResultsPoll(commit);
      commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', false);
    }
  };

  ag30RemainingPollInterval = setInterval(runTick, PROGRESS_POLL_INTERVAL_MS);
  runTick();
  ag30RemainingPollTimeout = setTimeout(() => {
    stopAutoGenerate30RemainingResultsPoll(commit);
  }, REMAINING_RESULTS_POLL_TIMEOUT_MS);
}

/**
 * Same shape as sdMixins.generateImage — start progress poll, await generate API only (poll continues in background).
 */
async function generateAutoGenerate30Test({ commit }, apiFunction, projectGenerateImageId, additionalFields, runContext, epoch) {
  const formData = new FormData();
  formData.append('project_generate_image_id', String(projectGenerateImageId));
  Object.entries(additionalFields).forEach(([key, value]) => {
    formData.append(key, value != null ? String(value) : '');
  });

  createAutoGenerate30IntervalProgress(projectGenerateImageId, commit);

  const generateResult = await apiFunction(formData);

  if (epoch !== autoGenerate30SessionEpoch) {
    return { status: null, data: null, skipped: true };
  }

  if (generateResult.status === constants.HTTP_UNPROCESSABLE_ENTITY) {
    abortAutoGenerate30ProgressPoll(commit);
    completeAutoGenerate30TestRun({ commit }, generateResult, { outcome: 'aborted', data: null }, runContext);
    return { status: generateResult.status, data: generateResult.data, failed: true };
  }
  if (generateResult.status === constants.HTTP_PERMISSION_DENIED) {
    abortAutoGenerate30ProgressPoll(commit);
    completeAutoGenerate30TestRun({ commit }, generateResult, { outcome: 'aborted', data: null }, runContext);
    return { status: generateResult.status, data: generateResult.data, failed: true };
  }
  if (generateResult.status !== constants.HTTP_CREATED) {
    abortAutoGenerate30ProgressPoll(commit);
    completeAutoGenerate30TestRun({ commit }, generateResult, { outcome: 'failure', data: generateResult.data }, runContext);
    return { status: generateResult.status, data: generateResult.data, failed: true };
  }

  const pollPromise = ag30PollOutcomePromise;
  if (pollPromise) {
    pollPromise.then((pollResult) => {
      if (epoch !== autoGenerate30SessionEpoch) {
        return;
      }
      completeAutoGenerate30TestRun({ commit }, generateResult, pollResult, runContext);
    });
  }

  return {
    status: generateResult.status,
    data: generateResult.data,
    started: true,
    project_id: runContext.resolvedProjectId,
  };
}

function completeAutoGenerate30TestRun({ commit }, generateResult, pollResult, runContext) {
  commit('SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING', false);
  commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);

  const { inputSnapshotForRun } = runContext;

  if (pollResult.outcome === 'cancelled' || pollResult.outcome === 'aborted') {
    commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
    return;
  }

  const images = pollResult.outcome === 'success' && Array.isArray(pollResult.images)
    ? pollResult.images
    : [];
  const batchOk = pollResult.outcome === 'success'
    && generateResult.status === constants.HTTP_CREATED
    && images.length >= OUTPUT_IMAGE_COUNT;

  if (batchOk) {
    const outputImages = images.slice(0, OUTPUT_IMAGE_COUNT);
    commit('SET_AUTO_GENERATE_30_OUTPUT_IMAGES', outputImages);
    commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT', inputSnapshotForRun);
    commit('SET_AUTO_GENERATE_30_PROGRESS', 100);
    commit('SET_AUTO_GENERATE_30_TEST_ERROR_MESSAGE', null);
    commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
    return;
  }

  failAutoGenerate30TestRun({ commit }, generateResult, pollResult);
  commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
}

function buildCreateFormData(form, { projectId, projectName, cancelProjectGenerateImageId }) {
  const formData = new FormData();
  if (projectId) {
    formData.append('project_id', String(projectId));
  }
  formData.append('project_name', projectName || '');
  if (cancelProjectGenerateImageId) {
    formData.append('cancel_project_generate_image_id', String(cancelProjectGenerateImageId));
  }
  Object.entries(buildAutoGenerate30GenerateFields(form)).forEach(([key, value]) => {
    formData.append(key, value != null ? String(value) : '');
  });
  if (form.reference_image) {
    formData.append('reference_image', form.reference_image);
  }
  return formData;
}

export function buildAutoGenerate30InputSnapshot(form) {
  const file = form.reference_image;
  let referenceFingerprint = null;
  if (file) {
    referenceFingerprint = `${file.name}:${file.size}:${file.lastModified}`;
  }
  return {
    category_shape: form.category_shape || '',
    concept: form.concept || '',
    brand: form.brand || '',
    naming: form.naming || '',
    catch_copy: form.catch_copy || '',
    additional_instructions: form.additional_instructions || '',
    reflection_level: form.reflection_level ?? REFLECTION_LEVEL_DEFAULT,
    has_reference_image: !!file,
    reference_fingerprint: referenceFingerprint,
  };
}

export function isAutoGenerate30InputDirty(form, snapshot) {
  if (!snapshot) {
    return false;
  }
  const current = buildAutoGenerate30InputSnapshot(form);
  return JSON.stringify(current) !== JSON.stringify(snapshot);
}

function resolveAutoGenerate30TestFailureMessage(generateResult, pollResult) {
  return normalizeBackendMessage(generateResult.data)
    || (pollResult.data && pollResult.data.error_message)
    || null;
}

function failAutoGenerate30TestRun({ commit }, generateResult, pollResult) {
  const message = resolveAutoGenerate30TestFailureMessage(generateResult, pollResult);
  if (message) {
    commit('SET_AUTO_GENERATE_30_TEST_ERROR_MESSAGE', message);
  }
  return {
    status: generateResult.status !== constants.HTTP_CREATED
      ? generateResult.status
      : constants.HTTP_OK,
    data: generateResult.data || pollResult.data,
    failed: true,
  };
}

let state = createInitialState();

const mutations = {
  SET_TYPE_EXPORT_PDF(state, type) {
    state.typeExportPdf = type;
  },
  SET_AUTO_GENERATE_30_STATUS(state, payload) {
    state.autoGenerate30Status = payload;
  },
  SET_AUTO_GENERATE_30_FORM_FIELD(state, { field, value }) {
    state.autoGenerate30Form = {
      ...state.autoGenerate30Form,
      [field]: value,
    };
  },
  MERGE_AUTO_GENERATE_30_EXTRACT(state, payload) {
    const next = { ...state.autoGenerate30Form };
    ['category_shape', 'concept', 'brand', 'catch_copy'].forEach((key) => {
      const value = payload && payload[key];
      if (typeof value === 'string' && value !== '') {
        next[key] = value;
      }
    });
    state.autoGenerate30Form = next;
  },
  SET_AUTO_GENERATE_30_REFERENCE_IMAGE(state, { file, preview }) {
    state.autoGenerate30Form = {
      ...state.autoGenerate30Form,
      reference_image: file,
      reference_image_preview: preview,
    };
  },
  CLEAR_AUTO_GENERATE_30_REFERENCE_IMAGE(state) {
    if (state.autoGenerate30Form.reference_image_preview) {
      URL.revokeObjectURL(state.autoGenerate30Form.reference_image_preview);
    }
    state.autoGenerate30Form = {
      ...state.autoGenerate30Form,
      reference_image: null,
      reference_image_preview: null,
      reflection_level: REFLECTION_LEVEL_DEFAULT,
    };
  },
  RESET_AUTO_GENERATE_30_FORM(state) {
    if (state.autoGenerate30Form.reference_image_preview) {
      URL.revokeObjectURL(state.autoGenerate30Form.reference_image_preview);
    }
    state.autoGenerate30Form = emptyForm();
  },
  SET_AUTO_GENERATE_30_EXTRACT_LOADING(state, value) {
    state.autoGenerate30ExtractLoading = value;
  },
  SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING(state, value) {
    state.autoGenerate30TestGenerateLoading = value;
  },
  SET_AUTO_GENERATE_30_TEST_POLL_ACTIVE(state, value) {
    state.autoGenerate30TestPollActive = value;
  },
  SET_AUTO_GENERATE_30_TEST_RUN_LOCKED(state, value) {
    state.autoGenerate30TestRunLocked = value;
  },
  SET_AUTO_GENERATE_30_PROGRESS(state, value) {
    state.autoGenerate30Progress = value;
  },
  SET_AUTO_GENERATE_30_OUTPUT_IMAGES(state, images) {
    state.autoGenerate30OutputImages = images;
  },
  SET_AUTO_GENERATE_30_INPUT_SNAPSHOT(state, snapshot) {
    state.autoGenerate30InputSnapshot = snapshot;
  },
  SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK(state, snapshot) {
    state.autoGenerate30InputSnapshotAtClick = snapshot;
  },
  SET_AUTO_GENERATE_30_TEST_ERROR_MESSAGE(state, message) {
    state.autoGenerate30TestErrorMessage = message;
  },
  SET_AUTO_GENERATE_30_SESSION(state, { projectId, projectGenerateImageId }) {
    state.autoGenerate30SessionProjectId = projectId;
    state.autoGenerate30ProjectGenerateImageId = projectGenerateImageId;
  },
  RESET_AUTO_GENERATE_30_TEST_RUN(state) {
    state.autoGenerate30Progress = 0;
    state.autoGenerate30OutputImages = [];
    state.autoGenerate30TestErrorMessage = null;
  },
  SET_AUTO_GENERATE_30_RESULTS(state, payload) {
    const prevMinutes = state.autoGenerate30Results
      && state.autoGenerate30Results.estimatedMinutes;
    state.autoGenerate30Results = {
      ...emptyAutoGenerate30Results(),
      ...payload,
      estimatedMinutes: payload.estimatedMinutes != null
        ? payload.estimatedMinutes
        : (prevMinutes != null ? prevMinutes : null),
    };
  },
  SET_AUTO_GENERATE_30_REMAINING_POLL_ACTIVE(state, value) {
    state.autoGenerate30RemainingPollActive = value;
  },
  SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED(state, value) {
    state.autoGenerate30RemainingSubmitLocked = value;
  },
  RESET_AUTO_GENERATE_30_STATE(state) {
    if (state.autoGenerate30Form.reference_image_preview) {
      URL.revokeObjectURL(state.autoGenerate30Form.reference_image_preview);
    }
    autoGenerate30SessionEpoch += 1;
    autoGenerate30StatusRequestId += 1;
    Object.assign(state, createInitialState());
  },
};

const actions = {
  updateTypeExportPdf({ commit }, type) {
    commit('SET_TYPE_EXPORT_PDF', type);
  },
  async fetchAutoGenerate30Status({ commit }, projectId = null) {
    const epoch = autoGenerate30SessionEpoch;
    const requestId = ++autoGenerate30StatusRequestId;
    const { status, data } = await getAutoGenerate30StatusApi(projectId);
    if (
      epoch !== autoGenerate30SessionEpoch
      || requestId !== autoGenerate30StatusRequestId
    ) {
      return { status: null, data: null, skipped: true };
    }
    if (status === 200) {
      commit('SET_AUTO_GENERATE_30_STATUS', data);
    } else {
      commit('SET_AUTO_GENERATE_30_STATUS', null);
    }
    return { status, data };
  },
  updateAutoGenerate30FormField({ commit }, payload) {
    commit('SET_AUTO_GENERATE_30_FORM_FIELD', payload);
  },
  setAutoGenerate30ReferenceImage({ commit }, payload) {
    commit('SET_AUTO_GENERATE_30_REFERENCE_IMAGE', payload);
  },
  clearAutoGenerate30ReferenceImage({ commit }) {
    commit('CLEAR_AUTO_GENERATE_30_REFERENCE_IMAGE');
  },
  resetAutoGenerate30Form({ commit }) {
    commit('RESET_AUTO_GENERATE_30_FORM');
  },
  async extractAutoGenerate30Document({ commit, state }, file) {
    if (state.autoGenerate30ExtractLoading) {
      return { status: null, data: null, skipped: true };
    }
    const epoch = autoGenerate30SessionEpoch;
    commit('SET_AUTO_GENERATE_30_EXTRACT_LOADING', true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { status, data } = await extractProductInformationDocumentApi(formData);
      if (epoch !== autoGenerate30SessionEpoch) {
        return { status: null, data: null, skipped: true };
      }
      if (status === 200 && data) {
        commit('MERGE_AUTO_GENERATE_30_EXTRACT', data);
      }
      return { status, data };
    } finally {
      if (epoch === autoGenerate30SessionEpoch) {
        commit('SET_AUTO_GENERATE_30_EXTRACT_LOADING', false);
      }
    }
  },
  /**
   * Like handleGenerateImageGemini: create (201) → loading on → generate API + progress poll in parallel.
   */
  async submitAutoGenerate30TestGenerate({ commit, state, getters }, payload = {}) {
    const busy = state.autoGenerate30TestRunLocked
      || state.autoGenerate30TestGenerateLoading
      || state.autoGenerate30TestPollActive;

    const replacingRunning = busy && getters.autoGenerate30InputDirtySinceTestClick;

    if (busy && !replacingRunning) {
      return { status: null, data: null, skipped: true };
    }

    if (replacingRunning) {
      abortAutoGenerate30ProgressPoll(commit);
      commit('SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING', false);
      commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);
    }

    const form = state.autoGenerate30Form;
    commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', true);
    commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', buildAutoGenerate30InputSnapshot(form));

    const projectName = payload.projectName || '';
    const projectId = payload.projectId || state.autoGenerate30SessionProjectId || null;
    const cancelProjectGenerateImageId = state.autoGenerate30ProjectGenerateImageId || null;

    const epoch = autoGenerate30SessionEpoch;
    try {
      const inputSnapshotForRun = buildAutoGenerate30InputSnapshot(form);
      const createFormData = buildCreateFormData(form, {
        projectId,
        projectName,
        cancelProjectGenerateImageId,
      });

      const { status, data } = await createProjectAutoGenerate30Api(createFormData);
      if (epoch !== autoGenerate30SessionEpoch) {
        return { status: null, data: null, skipped: true };
      }
      if (status !== constants.HTTP_CREATED) {
        commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);
        commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
        return { status, data };
      }

      const projectGenerateImage = data.project_generate_image;
      if (!projectGenerateImage || !projectGenerateImage.id) {
        commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);
        commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
        return { status: constants.HTTP_BAD_REQUEST, data: { message: 'Invalid create response' } };
      }

      const resolvedProjectId = data.id;
      const projectGenerateImageId = projectGenerateImage.id;
      commit('SET_AUTO_GENERATE_30_SESSION', {
        projectId: resolvedProjectId,
        projectGenerateImageId,
      });
      commit('SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING', true);
      commit('RESET_AUTO_GENERATE_30_TEST_RUN');

      return generateAutoGenerate30Test(
        { commit },
        generateImageGenerate30TestApi,
        projectGenerateImageId,
        buildAutoGenerate30GenerateFields(form),
        { inputSnapshotForRun, resolvedProjectId },
        epoch,
      );
    } catch (err) {
      abortAutoGenerate30ProgressPoll(commit);
      commit('SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING', false);
      commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);
      commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
      throw err;
    }
  },
  abortAutoGenerate30ProgressPoll({ commit }) {
    abortAutoGenerate30ProgressPoll(commit);
    commit('SET_AUTO_GENERATE_30_TEST_GENERATE_LOADING', false);
    commit('SET_AUTO_GENERATE_30_TEST_RUN_LOCKED', false);
    commit('SET_AUTO_GENERATE_30_INPUT_SNAPSHOT_AT_CLICK', null);
  },
  async submitAutoGenerate30RemainingGenerate({ commit, state }) {
    if (state.autoGenerate30RemainingSubmitLocked) {
      return { status: null, data: null, skipped: true };
    }
    const projectGenerateImageId = state.autoGenerate30ProjectGenerateImageId;
    if (!projectGenerateImageId) {
      return { status: constants.HTTP_BAD_REQUEST, data: null };
    }
    const epoch = autoGenerate30SessionEpoch;
    commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', true);
    try {
      const { status, data } = await generateImageGenerate30RemainingApi({
        project_generate_image_id: projectGenerateImageId,
      });
      if (epoch !== autoGenerate30SessionEpoch) {
        return { status: null, data: null, skipped: true };
      }
      if (status === constants.HTTP_OK && data) {
        applyAutoGenerate30ResultsPayload(commit, data.project_id, {
          status: constants.PROJECT_STATUS_PENDING,
          progress: 0,
          processed_images: 0,
          project_id: data.project_id,
          project_generate_image_id: data.project_generate_image_id,
          images: [],
          estimated_minutes: data.estimated_minutes,
        });
      } else {
        commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', false);
      }
      return { status, data };
    } catch (err) {
      commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', false);
      throw err;
    }
  },
  async fetchAutoGenerate30Results({ commit }, projectId) {
    const epoch = autoGenerate30SessionEpoch;
    const { status, data } = await getAutoGenerate30ResultsApi(projectId);
    if (epoch !== autoGenerate30SessionEpoch) {
      return { status: null, data: null, skipped: true };
    }
    if (status === constants.HTTP_PERMISSION_DENIED) {
      markAutoGenerate30ResultsForbidden(commit);
      return { status, data, forbidden: true };
    }
    if (status === constants.HTTP_OK && data) {
      applyAutoGenerate30ResultsPayload(commit, projectId, data);
      const imageCount = Array.isArray(data.images) ? data.images.length : 0;
      const terminalSuccess = data.status === constants.PROJECT_STATUS_SUCCESS
        && imageCount >= TOTAL_OUTPUT_IMAGE_COUNT;
      const terminalFailure = data.status === constants.PROJECT_STATUS_FAILURE
        || data.status === constants.PROJECT_STATUS_CANCEL;
      if (
        (data.status === constants.PROJECT_STATUS_PROGRESS
          || data.status === constants.PROJECT_STATUS_PENDING)
        && !terminalSuccess
      ) {
        createAutoGenerate30RemainingResultsPoll(projectId, commit);
      } else {
        stopAutoGenerate30RemainingResultsPoll(commit);
        if (terminalFailure) {
          commit('SET_AUTO_GENERATE_30_REMAINING_SUBMIT_LOCKED', false);
        }
      }
    }
    return { status, data, forbidden: false };
  },
  abortAutoGenerate30RemainingResultsPoll({ commit }) {
    stopAutoGenerate30RemainingResultsPoll(commit);
  },
  resetAutoGenerate30GlobalState({ commit }) {
    abortAutoGenerate30ProgressPoll(commit);
    stopAutoGenerate30RemainingResultsPoll(commit);
    commit('RESET_AUTO_GENERATE_30_STATE');
    commit('CLEAR_AUTO_GENERATE_30_TO_GENERATE_TAB', null, { root: true });
  },
  async removeBackgroundAutoGenerate30ForSurvey(_ctx, imagesResultId) {
    return deletingBackgroundApi({ images_result_id: imagesResultId });
  },
  async downloadAutoGenerate30Zip(_ctx, { projectId, projectName, indexImages, isAll }) {
    const projectGenerateImageId = _ctx.state.autoGenerate30Results.projectGenerateImageId
      || _ctx.state.autoGenerate30ProjectGenerateImageId;
    if (!projectId || !projectGenerateImageId) {
      return { status: constants.HTTP_BAD_REQUEST, data: null };
    }
    const formData = new FormData();
    formData.append('project_id', String(projectId));
    formData.append('project_generate_id', String(projectGenerateImageId));
    const fileNameImage = getFileNameGenerate(projectName || '', '');
    formData.append('file_name_image', fileNameImage);
    if (!isAll && Array.isArray(indexImages) && indexImages.length > 0) {
      formData.append('index_images', JSON.stringify(indexImages));
    }
    return downloadZipProjectGenerate(formData);
  },
  async downloadAutoGenerate30File(_ctx, { projectId, imageIndex }) {
    const projectGenerateImageId = _ctx.state.autoGenerate30Results.projectGenerateImageId
      || _ctx.state.autoGenerate30ProjectGenerateImageId;
    if (!projectId || !projectGenerateImageId || imageIndex == null) {
      return { status: constants.HTTP_BAD_REQUEST, data: null };
    }
    const formData = new FormData();
    formData.append('project_id', String(projectId));
    formData.append('project_generate_id', String(projectGenerateImageId));
    formData.append('image', String(imageIndex));
    return downloadFileProjectGenerate(formData);
  },
};

const getters = {
  typeExportPdf(state) {
    return state.typeExportPdf;
  },
  autoGenerate30Status(state) {
    return state.autoGenerate30Status;
  },
  autoGenerate30Form(state) {
    return state.autoGenerate30Form;
  },
  autoGenerate30ExtractLoading(state) {
    return state.autoGenerate30ExtractLoading;
  },
  autoGenerate30TestGenerateLoading(state) {
    return state.autoGenerate30TestGenerateLoading;
  },
  autoGenerate30Progress(state) {
    return state.autoGenerate30Progress;
  },
  autoGenerate30OutputImages(state) {
    return state.autoGenerate30OutputImages;
  },
  autoGenerate30InputSnapshot(state) {
    return state.autoGenerate30InputSnapshot;
  },
  autoGenerate30TestErrorMessage(state) {
    return state.autoGenerate30TestErrorMessage;
  },
  autoGenerate30SessionProjectId(state) {
    return state.autoGenerate30SessionProjectId;
  },
  autoGenerate30InputDirty(state) {
    return isAutoGenerate30InputDirty(state.autoGenerate30Form, state.autoGenerate30InputSnapshot);
  },
  autoGenerate30InputDirtySinceTestClick(state) {
    if (!state.autoGenerate30InputSnapshotAtClick) {
      return false;
    }
    return isAutoGenerate30InputDirty(state.autoGenerate30Form, state.autoGenerate30InputSnapshotAtClick);
  },
  autoGenerate30ShowDirtyInputMessage(state, getters) {
    if (!state.autoGenerate30InputSnapshotAtClick && !state.autoGenerate30InputSnapshot) {
      return false;
    }
    if (state.autoGenerate30InputSnapshotAtClick && getters.autoGenerate30InputDirtySinceTestClick) {
      return true;
    }
    const busy = state.autoGenerate30TestRunLocked
      || state.autoGenerate30TestGenerateLoading
      || state.autoGenerate30TestPollActive;
    if (!busy && state.autoGenerate30InputSnapshot && getters.autoGenerate30InputDirty) {
      return true;
    }
    return false;
  },
  autoGenerate30CanSubmitTestGenerate(state, getters) {
    const form = state.autoGenerate30Form;
    const hasRequired = ['category_shape', 'concept', 'brand'].every(
      (field) => String(form[field] || '').trim(),
    );
    if (!hasRequired) {
      return false;
    }
    // Changed input after clicking test generate — allow re-submit even while prior run is in flight.
    if (getters.autoGenerate30InputDirtySinceTestClick) {
      return true;
    }
    if (state.autoGenerate30TestGenerateLoading || state.autoGenerate30TestPollActive) {
      return false;
    }
    // After a failed run, allow retry without forcing the user to edit fields.
    if (state.autoGenerate30TestErrorMessage) {
      return true;
    }
    if (!state.autoGenerate30InputSnapshotAtClick) {
      return true;
    }
    const testBatchSucceeded = state.autoGenerate30OutputImages.length >= OUTPUT_IMAGE_COUNT
      && state.autoGenerate30InputSnapshot
      && !state.autoGenerate30TestErrorMessage;
    return testBatchSucceeded;
  },
  autoGenerate30Results(state) {
    return state.autoGenerate30Results;
  },
  autoGenerate30RemainingPollActive(state) {
    return state.autoGenerate30RemainingPollActive;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
